/**
 * Keymap editor view model (W2-13 settings-keymap): the rows of keymapModel.ts with everything the editor shows
 * and searches translated — command titles (with their argument), categories, contexts in words, shortcut labels in
 * the user's keyboard layout, and conflicts with their explanations (red = real conflict in the same situation, must
 * be resolved; yellow = a key shared with another situation, which the situation resolves: information, not a
 * problem). The Settings page uses it for search results and section counts, the editor to render.
 * A row's keys are grouped by chord (`chips`): the same key bound for several situations (Delete for notes and for
 * markers) is one chip listing its situations, and the explanations are listed once each (UX-061).
 */

import type { AppServices, CommandCategory, CommandDef, KeyBinding, KeymapPresetId } from '../../app/registry/types';
import type { Json } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import {
  buildRows,
  describeWhen,
  matchesTerms,
  normChord,
  presetListsOf,
  searchTerms,
  triggerOf,
  type ContextPart,
  type KeymapFilter,
  type KeymapRow,
  type RowBinding,
} from './keymapModel';
import type { Translate } from './settingsCatalog';
import { deepEqual } from './settingsValue';
import { m } from './settings.messages';

export const CATEGORY_LABELS: Readonly<Record<CommandCategory, MessageKey>> = {
  File: m.catFile,
  Edit: m.catEdit,
  Select: m.catSelect,
  View: m.catView,
  Playback: m.catPlayback,
  Notes: m.catNotes,
  Motion: m.catMotion,
  Lines: m.catLines,
  Timing: m.catTiming,
  Tools: m.catTools,
  Publish: m.catPublish,
  Help: m.catHelp,
};

const CATEGORY_ORDER: readonly CommandCategory[] = [
  'File',
  'Edit',
  'Select',
  'View',
  'Playback',
  'Notes',
  'Motion',
  'Lines',
  'Timing',
  'Tools',
  'Publish',
  'Help',
];

const REGION_LABELS: Readonly<Record<string, MessageKey>> = {
  lane: m.ctxLane,
  motion: m.ctxMotion,
  curve: m.ctxCurve,
  preview: m.ctxPreview,
  lines: m.ctxLines,
  inspector: m.ctxInspector,
  timing: m.ctxTiming,
  dock: m.ctxDock,
  problems: m.ctxProblems,
  history: m.ctxHistory,
  assets: m.ctxAssets,
  raw: m.ctxRaw,
  publish: m.ctxPublish,
  library: m.ctxLibrary,
  settings: m.ctxSettings,
};

const ATOM_LABELS: Readonly<Record<string, MessageKey>> = {
  textInput: m.ctxTextInput,
  dialog: m.ctxDialog,
  paletteOpen: m.ctxPalette,
  'placing.hold': m.ctxPlacingHold,
  'placing.segment': m.ctxPlacingSegment,
  'paste.ghost': m.ctxPasteGhost,
  gesture: m.ctxGesture,
  playing: m.ctxPlaying,
  'sel.notes': m.ctxSelNotes,
  'sel.keys': m.ctxSelKeys,
  'sel.segments': m.ctxSelSegments,
  'sel.lines': m.ctxSelLines,
  'sel.bpm': m.ctxSelBpm,
  'sel.any': m.ctxSelAny,
  'sel.single': m.ctxSelSingle,
  'sel.mixed': m.ctxSelMixed,
  'column.focused': m.ctxColumnFocused,
  'project.open': m.ctxProjectOpen,
  'activeLine.uiMover': m.ctxUiMover,
  'activeLine.notesAllowed': m.ctxNotesAllowed,
};

const CMP_LABELS: Readonly<Record<string, MessageKey>> = {
  'workspace=timing': m.ctxWorkspaceTiming,
  'workspace=chart': m.ctxWorkspaceChart,
  'workspace=publish': m.ctxWorkspacePublish,
  'workspace=library': m.ctxLibrary,
  'workspace=settings': m.ctxSettings,
  'level=guided': m.ctxLevelGuided,
  'level=full': m.ctxLevelFull,
  'preset=default': m.ctxPresetDefault,
  'preset=rpe': m.ctxPresetRpe,
  'os=mac': m.ctxOsMac,
  'os=other': m.ctxOsOther,
  'tool=select': m.ctxToolSelect,
  'tool=tap': m.ctxToolTap,
  'tool=drag': m.ctxToolDrag,
  'tool=flick': m.ctxToolFlick,
  'tool=hold': m.ctxToolHold,
  'tool=eraser': m.ctxToolEraser,
};

/** "testPlay" -> "test play" (atoms registered by modules have no message of their own). */
function humanize(atom: string): string {
  return atom
    .replace(/[._-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
}

function partText(t: Translate, p: ContextPart): string {
  if (p.kind === 'atom') {
    const region = /^(?:focus|hover)\.(.+)$/.exec(p.atom)?.[1];
    if (region !== undefined) {
      const key = REGION_LABELS[region];
      if (key !== undefined) return t(key);
    }
    const key = ATOM_LABELS[p.atom];
    return key === undefined ? humanize(p.atom) : t(key);
  }
  const key = CMP_LABELS[`${p.name}=${p.value}`];
  const text = key === undefined ? `${p.name} ${p.value}` : t(key);
  return p.negated ? t(m.ctxNot, { what: text }) : text;
}

/** A binding's situation in words: "Note lane · Notes selected", "Everywhere", or the condition as written. */
export function contextText(t: Translate, ...conditions: ReadonlyArray<string | undefined>): string {
  const alts = describeWhen(...conditions);
  if (alts === null) {
    return conditions.filter((c): c is string => c !== undefined && c.trim() !== '').join(' && ');
  }
  if (alts.length === 1 && alts[0]?.length === 0) return t(m.ctxEverywhere);
  return alts
    .map((alt) => (alt.length === 0 ? t(m.ctxEverywhere) : alt.map((p) => partText(t, p)).join(' · ')))
    .join(t(m.ctxOrSeparator));
}

/**
 * Argument values in words, without the argument names: `{ steps: 1, keySteps: 1 }` -> "1",
 * `{ workspace: 'timing' }` -> "timing", `{ any: true }` -> "any" (a flag reads as its name), camelCase split.
 */
function jsonText(v: Json): string {
  const parts: string[] = [];
  const walk = (x: Json, key: string | null): void => {
    if (x === null) return;
    if (typeof x === 'boolean') {
      if (x && key !== null) parts.push(humanize(key));
      return;
    }
    if (typeof x === 'number') parts.push(String(x));
    else if (typeof x === 'string') parts.push(/^[a-z][a-zA-Z0-9]*$/.test(x) ? humanize(x) : x);
    else if (Array.isArray(x)) for (const y of x as readonly Json[]) walk(y, key);
    else for (const [k, y] of Object.entries(x)) walk(y, k);
  };
  walk(v, null);
  return [...new Set(parts)].join(', ');
}

/** Words for a binding's arguments: the palette option label when the command declares choices, else the values. */
export function argsText(
  tr: (ref: { readonly key: string }) => string,
  cmd: CommandDef | undefined,
  args: Json | undefined,
): string {
  if (args === undefined) return '';
  const spec = cmd?.args;
  if (spec?.kind === 'choice') {
    const hit = spec.options.find((o) => deepEqual(o.value, args));
    if (hit !== undefined) return tr(hit.label);
  }
  return jsonText(args);
}

export interface BindingView {
  readonly rb: RowBinding;
  /** Layout-aware shortcut label ("Ctrl+K", "Alt+," ...). */
  readonly label: string;
  readonly context: string;
  readonly conflicts: ReadonlyArray<{
    readonly level: 'red' | 'yellow';
    readonly with: KeyBinding;
    /** Title of the other command (with its argument). */
    readonly other: string;
    readonly context: string;
  }>;
}

/** One key of a row: a chord with every situation it is bound for in this row. */
export interface ChipView {
  readonly chord: string;
  readonly label: string;
  readonly items: readonly BindingView[];
  readonly level: 'red' | 'yellow' | null;
  /** Some binding of the chip is one of your additions. */
  readonly user: boolean;
  /** Its situations in words, joined. */
  readonly context: string;
}

/** One explanation line under a row: a conflict or a shared key, merged over the row's keys. */
export interface ConflictLine {
  readonly level: 'red' | 'yellow';
  /** Labels of the row's keys it concerns ("Del, Backspace"). */
  readonly labels: string;
  readonly other: string;
  readonly context: string;
}

export interface RowView {
  readonly row: KeymapRow;
  readonly title: string;
  readonly category: string;
  readonly categoryId: CommandCategory | null;
  readonly context: string;
  readonly bindings: readonly BindingView[];
  /** The bindings grouped by chord, in first-seen order. */
  readonly chips: readonly ChipView[];
  /** Conflict and shared-key explanations, each once. */
  readonly conflictLines: readonly ConflictLine[];
  readonly removed: ReadonlyArray<{ readonly binding: KeyBinding; readonly label: string }>;
  /** Your removals that do not touch the current preset's keys: the other preset's (`preset`), or stale (null). */
  readonly removedElsewhere: ReadonlyArray<{
    readonly binding: KeyBinding;
    readonly label: string;
    readonly preset: KeymapPresetId | null;
  }>;
  readonly worstConflict: 'red' | 'yellow' | null;
  /** Lower-cased search texts. */
  readonly haystack: readonly string[];
}

type RawConflicts = ReturnType<AppServices['keymap']['conflicts']>;

/**
 * Conflicts per binding, asked of the keymap once per keymap state: keyed by the effective binding list (the
 * Settings hooks keep one list per keymap state, shared by the page search and the editor) and by the binding
 * object of that list. A locale switch or a second view of the same state reuses them.
 */
const conflictCache = new WeakMap<
  AppServices['keymap'],
  WeakMap<readonly KeyBinding[], WeakMap<KeyBinding, RawConflicts>>
>();

function conflictsOf(
  keymap: AppServices['keymap'],
  effective: readonly KeyBinding[],
  binding: KeyBinding,
): RawConflicts {
  let byState = conflictCache.get(keymap);
  if (byState === undefined) {
    byState = new WeakMap();
    conflictCache.set(keymap, byState);
  }
  let byBinding = byState.get(effective);
  if (byBinding === undefined) {
    byBinding = new WeakMap();
    byState.set(effective, byBinding);
  }
  let hit = byBinding.get(binding);
  if (hit === undefined) {
    hit = keymap.conflicts(binding);
    byBinding.set(binding, hit);
  }
  return hit;
}

/** Title of a command and argument set ("Beat snap: 1/4"); the id when the command is unknown. */
export function commandTitle(
  t: Translate,
  tr: (ref: { readonly key: string }) => string,
  cmd: CommandDef | undefined,
  commandId: string,
  args: Json | undefined,
): string {
  const base = cmd === undefined ? commandId : t(cmd.title);
  const a = argsText(tr, cmd, args);
  return a === '' ? base : t(m.commandWithArgs, { command: base, args: a });
}

/** Builds the translated rows of the editor from the services' current state. */
export function buildKeymapView(
  services: Pick<AppServices, 'registry' | 'keymap' | 'settings'>,
  t: Translate,
  tr: (ref: { readonly key: string }) => string,
  snapshot: {
    readonly effective: readonly KeyBinding[];
    readonly overrides: readonly KeyBinding[];
    /** Preset in force (default: the keymap's). */
    readonly preset?: KeymapPresetId;
  } = { effective: services.keymap.bindings(), overrides: services.settings.value.keyboard.overrides },
): RowView[] {
  const commands = [...services.registry.commands()].sort((a, b) => {
    const ca = CATEGORY_ORDER.indexOf(a.category);
    const cb = CATEGORY_ORDER.indexOf(b.category);
    if (ca !== cb) return ca - cb;
    return t(a.title).localeCompare(t(b.title));
  });
  const presets = presetListsOf(services.registry.modules, snapshot.preset ?? services.keymap.preset);
  const rows = buildRows(commands, snapshot.effective, snapshot.overrides, presets);
  const commandOf = (id: string): CommandDef | undefined => services.registry.command(id);

  return rows.map((row) => {
    const cmd = row.command;
    const title = commandTitle(t, tr, cmd, row.commandId, row.args);
    const categoryId = cmd?.category ?? null;
    const category = categoryId === null ? t(m.catUnknown) : t(CATEGORY_LABELS[categoryId]);
    const bindings: BindingView[] = row.bindings.map((rb) => {
      const conflicts = conflictsOf(services.keymap, snapshot.effective, rb.binding).map((c) => {
        const otherCmd = commandOf(c.with.command);
        return {
          level: c.level,
          with: c.with,
          other: commandTitle(t, tr, otherCmd, c.with.command, c.with.args),
          context: contextText(t, c.with.when, otherCmd?.when),
        };
      });
      return {
        rb,
        label: services.keymap.label(rb.binding.chord),
        context: contextText(t, rb.binding.when, cmd?.when),
        conflicts,
      };
    });
    const contexts = [...new Set(bindings.map((b) => b.context))];
    const context = contexts.length > 0 ? contexts.join('; ') : contextText(t, cmd?.when);
    const removed = row.removed.map((r) => ({ binding: r, label: services.keymap.label(r.chord) }));
    const removedElsewhere = row.removedElsewhere.map((r) => ({
      binding: r.binding,
      label: services.keymap.label(r.binding.chord),
      preset: r.preset,
    }));
    const levels = bindings.flatMap((b) => b.conflicts.map((c) => c.level));
    const worstConflict = levels.includes('red') ? 'red' : levels.includes('yellow') ? 'yellow' : null;
    const chips = chipsOf(bindings);
    const conflictLines = conflictLinesOf(bindings);
    const haystack = [
      title,
      row.commandId,
      category,
      context,
      ...(cmd?.aliases ?? []),
      ...bindings.map((b) => b.label),
      ...bindings.map((b) => b.rb.binding.chord),
    ].map((s) => s.toLowerCase());
    return {
      row,
      title,
      category,
      categoryId,
      context,
      bindings,
      chips,
      conflictLines,
      removed,
      removedElsewhere,
      worstConflict,
      haystack,
    };
  });
}

function levelOf(items: readonly BindingView[]): 'red' | 'yellow' | null {
  const all = items.flatMap((b) => b.conflicts.map((c) => c.level));
  return all.includes('red') ? 'red' : all.includes('yellow') ? 'yellow' : null;
}

/** Bindings grouped by chord (first-seen order). */
export function chipsOf(bindings: readonly BindingView[]): ChipView[] {
  const groups = new Map<string, BindingView[]>();
  for (const b of bindings) {
    const k = normChord(b.rb.binding.chord);
    const g = groups.get(k);
    if (g === undefined) groups.set(k, [b]);
    else g.push(b);
  }
  return [...groups.values()].map((items) => {
    const first = items[0] as BindingView;
    return {
      chord: first.rb.binding.chord,
      label: first.label,
      items,
      level: levelOf(items),
      user: items.some((b) => b.rb.source === 'user'),
      context: [...new Set(items.map((b) => b.context))].join('; '),
    };
  });
}

/** Conflict lines of a row: one per level, other command and situation, naming every key of the row it concerns. */
export function conflictLinesOf(bindings: readonly BindingView[]): ConflictLine[] {
  const lines = new Map<string, { level: 'red' | 'yellow'; labels: string[]; other: string; context: string }>();
  for (const b of bindings) {
    for (const c of b.conflicts) {
      const k = `${c.level}\u0000${c.other}\u0000${c.context}`;
      const line = lines.get(k);
      if (line === undefined) lines.set(k, { level: c.level, labels: [b.label], other: c.other, context: c.context });
      else if (!line.labels.includes(b.label)) line.labels.push(b.label);
    }
  }
  return [...lines.values()]
    .map((l) => ({ level: l.level, labels: l.labels.join(', '), other: l.other, context: l.context }))
    .sort((a, b) => (a.level === b.level ? 0 : a.level === 'red' ? -1 : 1));
}

/** Rows with a real conflict (red) and rows sharing a key with another situation (yellow only). */
export function conflictCounts(rows: readonly RowView[]): { readonly conflicts: number; readonly shared: number } {
  let conflicts = 0;
  let shared = 0;
  for (const r of rows) {
    if (r.worstConflict === 'red') conflicts++;
    else if (r.worstConflict === 'yellow') shared++;
  }
  return { conflicts, shared };
}

/**
 * Rows that pass the search terms and the filter. `chord` (search by pressing keys) matches bound chords by their
 * trigger (a `hold:` key too); several chords may be given (the key position and the typed character).
 */
export function filterKeymapView(
  rows: readonly RowView[],
  opts: {
    readonly query: string;
    readonly filter: KeymapFilter;
    readonly chord?: string | readonly string[] | null;
  },
): RowView[] {
  const terms = searchTerms(opts.query);
  const chords =
    opts.chord === undefined || opts.chord === null
      ? null
      : new Set((typeof opts.chord === 'string' ? [opts.chord] : opts.chord).map(triggerOf));
  return rows.filter((r) => {
    if (opts.filter === 'changed' && !r.row.changed) return false;
    if (opts.filter === 'conflicts' && r.worstConflict !== 'red') return false;
    if (opts.filter === 'shared' && r.worstConflict !== 'yellow') return false;
    if (opts.filter === 'unbound' && r.bindings.length > 0) return false;
    if (chords !== null && !r.bindings.some((b) => chords.has(triggerOf(b.rb.binding.chord)))) return false;
    return matchesTerms(terms, r.haystack);
  });
}
